import { Injectable } from '@angular/core';
import {Observable, of, switchMap, throwError} from "rxjs";
import {AuthService} from "./auth.service";
import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import {environment} from "../../environments/environment";
import {catchError, map, tap} from "rxjs/operators";
import {SearchPeopleParams} from "../state/advancedSearch/advanced-search.actions";

@Injectable({
  providedIn: 'root'
})
export class AdvancedSearchService {

  private apiUrl = environment.api.url;
  token: string;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  // Update your searchCompanies method in your service

  searchCompanies(
    filters: any,
    page: number = 1,
    pageSize: number = 10,
    nextPageToken?: string
  ): Observable<{ data: any }> {
    const url = `${this.apiUrl}/veridion-company-search`;

    // Fetch the token from AuthService
    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
      })
    };

    // Prepare the request payload
    const data: any = {
      filters,
      page,
      pageSize
    };

    // Add next page token if available
    if (nextPageToken) {
      data.pagination_token = nextPageToken; // Use the correct parameter name expected by your API
    }

    return this.http.post<any>(url, data, httpOptions).pipe(
      map((response) => {
        if (!response.data) {
          throw new Error('Advanced search failed: ' + response.message);
        }

        return {
          data: response.data
        };
      }),
      catchError((error) => {
        console.error('[advanced-search.service.ts] error', error);
        return throwError(() => error);
      })
    );
  }

  searchPeople(params: SearchPeopleParams): Observable<{ data: any[]; message: string }> {
    const url = `${this.apiUrl}/search/fuzzy`;

    // Fetch the token from AuthService
    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
      }),
    };

    // Build the request payload with all parameters
    const data: any = {
      q_term: params.companyName,
      page: params.page || 1,
      per_page: params.perPage || 10
    };

    // Only add optional parameters if they exist
    if (params.person_titles && params.person_titles.length > 0) {
      data.person_titles = params.person_titles;
    }

    if (params.person_seniorities && params.person_seniorities.length > 0) {
      data.person_seniorities = params.person_seniorities;
    }

    // person_locations is now a string, not an array
    if (params.person_locations && params.person_locations.trim() !== '') {
      data.person_locations = params.person_locations;
    }

    if (params.organization_locations && params.organization_locations.length > 0) {
      data.organization_locations = params.organization_locations;
    }

    return this.http.post<any>(url, data, httpOptions).pipe(
      map((response) => {
        if (!response.data) {
          throw new Error('Fuzzy search failed: ' + response.message);
        }

        return {
          data: response.data,
          message: response.message,
        };
      }),
      catchError((error) => {
        console.error('[fuzzy-search.service.ts] error', error);
        return throwError(error);
      })
    );
  }

  searchPeopleByDomain(params: SearchPeopleParams): Observable<{ data: any; message: string }> {
    const url = `${this.apiUrl}/search/by-domain`;

    // Fetch the token from AuthService
    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
      }),
    };

    // Build the request payload per API docs
    const data: any = {
      domains: [params.website_domain],
      page: params.page || 1,
      per_page: params.perPage || 10
    };

    // Only add optional parameters if they exist
    if (params.person_titles && params.person_titles.length > 0) {
      data.person_titles = params.person_titles;
    }

    if (params.person_seniorities && params.person_seniorities.length > 0) {
      data.person_seniorities = params.person_seniorities;
    }

    return this.http.post<any>(url, data, httpOptions).pipe(
      map((response) => {
        if (!response.data) {
          throw new Error('Search by domain failed: ' + response.message);
        }

        // Normalize response to match the fuzzy search structure
        // so the reducer (which reads response.data.apollo_result) works unchanged
        return {
          data: {
            apollo_result: {
              people: response.data.people || [],
              pagination: response.data.pagination || null,
              companies: response.data.companies || [],
              contacts: response.data.contacts || []
            }
          },
          message: response.message || response.data?.metadata?.message || '',
        };
      }),
      catchError((error) => {
        console.error('[advanced-search.service.ts] search by domain error', error);
        return throwError(error);
      })
    );
  }

  // In advanced-search.service.ts
  /*
  searchLocations(country: string = '', region: string = '', city: string = ''): Observable<any> {
    const url = `${this.apiUrl}/veridion-search-locations`;

    // Fetch the token from AuthService
    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
      })
    };

    // Create request body - if country is empty, we'll get all countries
    const requestBody: any = {};

    // Only add parameters if they have values
    if (country) requestBody.country = country;
    if (region) requestBody.region = region;
    if (city) requestBody.city = city;

    console.log('Searching locations with:', requestBody);

    return this.http.post<any>(url, requestBody, httpOptions).pipe(
      map((response) => {
        console.log('Location search response:', response);

        if (!response) {
          return { data: [] };
        }

        return {
          data: response.data || [],
          message: response.message
        };
      }),
      catchError((error) => {
        console.error('[advanced-search.service.ts] location search error', error);
        return of({ data: [], message: 'Error fetching location data' });
      })
    );
  }
  */

  // Get auth headers
  private getHttpOptions(): { headers: HttpHeaders } {
    // Ensure the token is available
    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
      })
    };
  }

  // Search for countries
  searchCountries(searchTerm: string = ''): Observable<any> {
    const url = `${this.apiUrl}/locations/countries`;

    // Add the 'name' parameter for country search
    let params: any = {};
    if (searchTerm) {
      params.name = searchTerm;
    }

    // console.log('Searching countries with term:', searchTerm);

    return this.http.get<any>(url, {
      ...this.getHttpOptions(),
      params
    }).pipe(
      // tap(rawResponse => console.log('Raw API response:', rawResponse)),
      map((response) => {
        // console.log('Processing response in service:', response);

        // Return the response directly without additional wrapping
        return response;
      }),
      catchError((error) => {
        console.error('[search.service.ts] countries search error', error);
        return of({ data: { countries: [] }, message: 'Error fetching country data' });
      })
    );
  }

  // Search for regions within a country
  searchRegions(countryName: string, veridionCountryId: string | number, searchTerm: string = ''): Observable<any> {
    const url = `${this.apiUrl}/locations/countries/${encodeURIComponent(countryName)}/regions`;

    // Add required parameters for region search
    let params: any = {
      veridion_country_id: veridionCountryId
    };

    if (searchTerm) {
      params.name = searchTerm;
    }

    console.log(`Searching regions in ${countryName} (ID: ${veridionCountryId}) with term:`, searchTerm);

    return this.http.get<any>(url, {
      ...this.getHttpOptions(),
      params
    }).pipe(
      map((response) => {
        console.log('Regions search response:', response);

        if (!response) {
          return { data: [] };
        }

        return {
          data: response.data || [],
          message: response.message
        };
      }),
      catchError((error) => {
        console.error('[search.service.ts] regions search error', error);
        return of({ data: [], message: 'Error fetching region data' });
      })
    );
  }

  // Search for cities within a region
  searchCities(
    countryName: string,
    regionName: string,
    veridionCountryId: string | number,
    veridionRegionId: string | number,
    searchTerm: string = ''
  ): Observable<any> {
    const url = `${this.apiUrl}/locations/countries/${encodeURIComponent(countryName)}/regions/${encodeURIComponent(regionName)}/cities`;

    // Add required parameters for city search
    let params: any = {
      veridion_country_id: veridionCountryId,
      veridion_region_id: veridionRegionId
    };

    if (searchTerm) {
      params.name = searchTerm;
    }

    console.log(`Searching cities in ${regionName}, ${countryName} (Country ID: ${veridionCountryId}, Region ID: ${veridionRegionId}) with term:`, searchTerm);

    return this.http.get<any>(url, {
      ...this.getHttpOptions(),
      params
    }).pipe(
      map((response) => {
        console.log('Cities search response:', response);

        if (!response) {
          return { data: [] };
        }

        return {
          data: response.data || [],
          message: response.message
        };
      }),
      catchError((error) => {
        console.error('[search.service.ts] cities search error', error);
        return of({ data: [], message: 'Error fetching city data' });
      })
    );
  }

  // Keep the original method for backward compatibility if needed
  searchLocations(country: string = '', region: string = '', city: string = ''): Observable<any> {
    console.warn('searchLocations is deprecated. Use searchCountries, searchRegions, or searchCities instead.');

    // This is a simplified version of the original method for backward compatibility
    // It won't work properly with the new API structure that requires IDs
    return this.searchCountries(country);
  }

  /**
   * Search for company industries
   * @param searchTerm Optional search term to filter industries
   */
  /**
   * Search for company industries
   * @param searchTerm Optional search term to filter industries
   */
  searchCompanyIndustries(searchTerm: string = ''): Observable<any> {
    // Construct the query parameters
    let params = new HttpParams();
    if (searchTerm) {
      params = params.set('q', searchTerm);
    }

    const url = `${this.apiUrl}/veridion-industries`;

    // Fetch the token from AuthService
    return this.authService.token().pipe(
      switchMap(token => {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }),
          params
        };

        // Make the authenticated request
        return this.http.get<any>(url, httpOptions);
      }),
      catchError(error => {
        console.error('Error fetching company industries:', error);
        return throwError(() => new Error('Failed to fetch company industries'));
      })
    );
  }

}
